import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { TranslationService } from "../../services/TranslationService";
import { ToastService } from "../../pages/shared/bootstrap/Toast";
import {
  ClientFields,
  getClientFieldTitle
} from "../../services/ClientService";
import {
  EntityField,
  sortEntityFields
} from "../../pages/shared/entities/EntityField";
import WhatsappService, {
  WhatsappTemplateItemType
} from "../../services/WhatsappService";
import CompanyService, { Entities } from "../../services/CompanyService";
import ActivityService from "../../services/ActivityService";
import { WhatsappChat } from "./whatsappChat/WhatsappChat";
import { WhatsappInputSend } from "./whatsappChat/WhatsappInputSend";
import { ChatItem, WhatsappComposeType } from "./typings";
import { getDefaultActivityType } from "./utils/utils";
import { ConfirmationModal } from "./ConfirmationModal";
import "../whatsapp/whatsappChat/styles.css";
import { WhatsAppRequestBody } from "../../pages/client/activity/entities/AcitivityPendingResponse";
import CounterMsgContext from "../../pages/shared/CounterMsgContext";
import {
  useSocketEvent,
  WhatsAppNotificationEvent
} from "../../pages/shared/SocketContext";

export enum WHATSTAPP_STATUS {
  IS_NEW = "-1",
  OPEN = "0",
  DERIVATED = "1",
  CLOSED = "2"
}

export const WhatsappCompose = ({
  person,
  mode,
  onSubmit,
  initialMessage,
  onMessageSent,
  onMainMessageState,
  onRegisterActivityTypeSetter,
  onRegisterPhoneSetter,
  onRegisterPhoneLoadingSetter,
  whatsappPhone,
  onChangeStatus
}: WhatsappComposeType) => {
  const { getCounterInbox, flagRefreshChats, setFlagRefreshChats } =
    useContext(CounterMsgContext);

  const [firstMount, setFirstMount] = useState(true);
  const [whatsAppTemplateSelected, setWhatsAppTemplateSelected] = useState<
    WhatsappTemplateItemType | "0" | null
  >("0");
  const [matrizResults, setMatrizResults] = useState<any[][]>([[]]);

  // ------------------------------
  // Estado UI y helpers
  // ------------------------------
  const [whatsAppTemplate, setWhatsAppTemplate] = useState<string | null>(null);
  const [changeSMSSender, setChangeSMSSender] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [chatStatus, setChatStatus] = useState<string>(undefined);
  const [activityTypeIdState, setActivityTypeIdState] = useState<number | "">(
    ""
  );
  const [isFreeText, setIsFreeText] = useState(false);
  const [originalOpenChat, setOriginalOpenChat] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  // Teléfono efectivo: para mensajes existentes usa SMSPhone del mainMessage, para nuevos usa el del cliente
  const [effectivePhone, setEffectivePhone] = useState<string | undefined>(
    whatsappPhone
  );
  // Referencia al setter del header para actualizar el teléfono mostrado
  const setHeaderPhoneRef = useRef<
    ((phone: string | undefined) => void) | null
  >(null);
  // Referencia al setter del header para controlar el loading del teléfono
  const setHeaderPhoneLoadingRef = useRef<((loading: boolean) => void) | null>(
    null
  );
  const isFinalizingRef = useRef(false);

  const personId = person.PersonId;
  const isHistory = mode.kind === "history";

  // ------------------------------
  // Data para pildorita de tipo de actividad
  // ------------------------------
  const activityTypeOptions = useMemo(
    () =>
      CompanyService.getActivityTypes()
        .filter((x) => x.SMS)
        .map((x) => ({ value: x.ActivityTypeID, text: x.ActivityTypeName })),
    []
  );

  const activityTypeLabel = useMemo(() => {
    const selectedId =
      activityTypeIdState !== "" && activityTypeIdState != null
        ? Number(activityTypeIdState)
        : null;

    const effectiveId =
      selectedId ??
      (getDefaultActivityType() ? Number(getDefaultActivityType()) : null);

    if (!effectiveId) return null;

    return (
      activityTypeOptions.find((o) => o.value === effectiveId)?.text ?? null
    );
  }, [activityTypeOptions, activityTypeIdState]);

  // ------------------------------
  // Reglas de 24h SOLO por Inbound (clave)
  // ------------------------------
  const getLastInboundDate = useCallback((): Date | null => {
    let latest: Date | null = null;
    for (const pageItems of matrizResults) {
      for (const it of pageItems as ChatItem[]) {
        if (!(it as any).Inbound) continue;
        const d = new Date(it.Created);
        if (!latest || d > latest) latest = d;
      }
    }

    return latest;
  }, [matrizResults]);

  const isLastInboundUnder24h = useMemo(() => {
    const last = getLastInboundDate();
    if (!last) return false;
    const res = new Date().getTime() - last.getTime() < 24 * 60 * 60 * 1000;
    return res;
  }, [getLastInboundDate]);

  const canShowFinalizeBtn =
    chatStatus === WHATSTAPP_STATUS.DERIVATED && isLastInboundUnder24h;

  const handleTemplateWhatsappChange = (value: string | null) => {
    setWhatsAppTemplate(value);
  };

  const canUseFreeText =
    chatStatus === WHATSTAPP_STATUS.DERIVATED || originalOpenChat;

  // Prefill the free text box once the conversation is loaded. Closed or new
  // conversations only accept approved templates, so there is nothing to prefill.
  const initialMessageApplied = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialMessageApplied.current || isFetching) {
      return;
    }
    initialMessageApplied.current = true;
    if (canUseFreeText) {
      handleFreeTextChange(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, isFetching, canUseFreeText]);

  const handlePickTemplate = (item: WhatsappTemplateItemType | "0") => {
    if (item === "0") {
      setWhatsAppTemplateSelected("0");
      setWhatsAppTemplate("");
      setIsFreeText(canUseFreeText);
      return;
    }

    setIsFreeText(false);

    const resultItem = item as WhatsappTemplateItemType;
    const parameters = resultItem.Parameters ?? 0;
    let texto = resultItem.Structure ?? "";

    const clientFieldOptions = [
      ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client).map(
        (x) => new EntityField(x)
      )
    ]
      .sort(sortEntityFields(CompanyService.getPersonSortedFields().split(",")))
      .map((x) => ({ value: x.id.toString(), text: x?.title ?? "" }));

    const fixedFieldsCustomer = [
      { text: TranslationService.translate.InsertField, value: "" },
      ...WhatsappService.getConfigItemsByWhatsApp().map((cl) => ({
        value: cl.toString(),
        text: getClientFieldTitle(cl as ClientFields)
      })),
      ...clientFieldOptions
    ];

    if (resultItem.FieldList) {
      (
        resultItem.FieldList.split(",").map((f, index) => ({
          param: index + 1,
          data: `${f}`,
          valueText: `${f}`
        })) ||
        Array.from({ length: parameters }, (_, i) => ({
          param: i + 1,
          data: `{{${i + 1}}}`,
          valueText: `{{${i + 1}}}`
        }))
      ).forEach(({ param, valueText }) => {
        texto = texto.replace(
          `{{${param}}}`,
          `{{${fixedFieldsCustomer.find((f) => f.value === valueText)?.text || valueText}}}`
        );
      });
    }

    setWhatsAppTemplateSelected(resultItem);
    handleTemplateWhatsappChange(texto);
  };

  // ------------------------------
  // Envío de mensaje (respeta modo de carga, pero valida negocio)
  // ------------------------------
  const handleCreateWhatsAppMessage = async () => {
    setSendingWhatsApp(true);

    if (personId && effectivePhone) {
      const freeTextCompleted =
        isFreeText && (whatsAppTemplate?.length ?? 0) > 0;
      const hasTemplateSelected =
        !isFreeText &&
        whatsAppTemplateSelected &&
        whatsAppTemplateSelected !== "0" &&
        typeof (whatsAppTemplateSelected as WhatsappTemplateItemType)
          .WATemplateID === "number";

      const templateId = hasTemplateSelected
        ? Number(
            (whatsAppTemplateSelected as WhatsappTemplateItemType)
              .WATemplateID ?? 0
          )
        : 0;

      if (!freeTextCompleted && !hasTemplateSelected) {
        ToastService.showToast(
          TranslationService.translate.SelectATemplateAndFillRequiredFields,
          "",
          "info"
        );
        setSendingWhatsApp(false);
        return;
      }

      if (!freeTextCompleted && templateId <= 0) {
        ToastService.showToast(
          TranslationService.translate.SelectATemplateAndFillRequiredFields,
          "",
          "info"
        );
        setSendingWhatsApp(false);
        return;
      }

      if (whatsAppTemplate !== null) {
        const selectedActivityTypeId =
          activityTypeIdState === "" || activityTypeIdState == null
            ? null
            : Number(activityTypeIdState);

        const defaultActivityType = getDefaultActivityType();
        const activityTypeIdForBody =
          selectedActivityTypeId ??
          (defaultActivityType ? Number(defaultActivityType) : null);

        const dataBodyWhatsApp: WhatsAppRequestBody = {
          personid: personId,
          content: freeTextCompleted
            ? (whatsAppTemplate ?? "")
            : (whatsAppTemplateSelected as WhatsappTemplateItemType).Structure,
          phone: effectivePhone!,
          activitytypeid: activityTypeIdForBody,
          groupid: null,
          watemplateid: freeTextCompleted ? 0 : templateId
        };

        const result = await ActivityService.setWhatsApp(dataBodyWhatsApp);

        if (result instanceof Error || result.Response !== "Ok") {
          ToastService.showToast(
            TranslationService.translate.WhatsAppErrorSent,
            "",
            "danger"
          );
          setSendingWhatsApp(false);
          return;
        }

        setWhatsAppTemplate(null);
        setWhatsAppTemplateSelected("0");
        setIsFreeText(false);
        setActivityTypeIdState("");
        if (!isHistory) {
          setChatStatus(WHATSTAPP_STATUS.OPEN);
        }

        // Recargar la conversación después de enviar
        if (isHistory) {
          await fetchHistoryOnce();
        } else {
          await fetchActiveConversation();
        }

        onMessageSent && onMessageSent();
        onSubmit && onSubmit();
        setSendingWhatsApp(false);
      } else {
        ToastService.showToast(
          TranslationService.translate.SelectATemplateAndFillRequiredFields,
          "",
          "info"
        );
        setSendingWhatsApp(false);
      }
    } else {
      ToastService.showToast(
        TranslationService.translate.ClientPhoneNotConfigured,
        "",
        "info"
      );
      setSendingWhatsApp(false);
    }
  };

  const fetchData = async (fn: () => Promise<void> | void) => {
    setIsFetching(true);
    try {
      await fn();
    } finally {
      setIsFetching(false);
      setChangeSMSSender(false);
      getCounterInbox();
      setFlagRefreshChats(false);
      onSubmit && onSubmit();
    }
  };

  const notifyHeaderMainMessage = useCallback(
    (hasMain: boolean, mainMessageId?: number) => {
      onMainMessageState?.(hasMain, mainMessageId);
    },
    [onMainMessageState]
  );

  const chatList = useCallback(
    (result: any): any[][] => {
      const hasMain = !!result?.mainMessage;
      notifyHeaderMainMessage(hasMain, result?.mainMessage?.MessageID);

      const main = result?.mainMessage ?? null;
      const openChat = result?.openchat || false;
      const history = Array.isArray(result?.messageHistory)
        ? result.messageHistory
        : [];

      if (main?.ActivityTypeID != null) {
        setActivityTypeIdState(Number(main.ActivityTypeID));
      }

      // Para mensajes existentes (history), usar SMSPhone del mainMessage si está disponible
      if (isHistory) {
        if (main?.SMSPhone) {
          setEffectivePhone(main.SMSPhone);
          // Actualizar el teléfono en el header
          setHeaderPhoneRef.current?.(main.SMSPhone);
        }
        // Desactivar loading del teléfono cuando se resuelve (tenga o no SMSPhone)
        setHeaderPhoneLoadingRef.current?.(false);
      } else if (!isHistory) {
        // Para nuevos mensajes (active), usar el teléfono del cliente
        setEffectivePhone(whatsappPhone);
        // Actualizar el teléfono en el header
        setHeaderPhoneRef.current?.(whatsappPhone);
        // Desactivar loading del teléfono (por si acaso estaba activo)
        setHeaderPhoneLoadingRef.current?.(false);
      }

      if (!hasMain) {
        setIsFreeText(false);
        setChatStatus(WHATSTAPP_STATUS.IS_NEW);
        setOriginalOpenChat(false);
        return [[]];
      }

      let statusStr =
        main?.ChatStatus !== null
          ? String(main.ChatStatus)
          : WHATSTAPP_STATUS.OPEN;

      // Validar ventana de 24h para DERIVATED y OPEN
      if (
        statusStr === WHATSTAPP_STATUS.DERIVATED ||
        statusStr === WHATSTAPP_STATUS.OPEN
      ) {
        let lastMessageDate: Date | null = null;

        if (statusStr === WHATSTAPP_STATUS.DERIVATED) {
          // Para DERIVATED: buscar solo el último mensaje inbound (entrante)
          for (const item of history) {
            if ((item as any).Inbound) {
              const d = new Date(item.Created);
              if (!lastMessageDate || d > lastMessageDate) {
                lastMessageDate = d;
              }
            }
          }
        } else if (statusStr === WHATSTAPP_STATUS.OPEN) {
          // Para OPEN: buscar el último mensaje (inbound o outbound)
          for (const item of history) {
            const d = new Date(item.Created);
            if (!lastMessageDate || d > lastMessageDate) {
              lastMessageDate = d;
            }
          }
        }

        // Si no hay mensajes en el historial, usar la fecha del mainMessage como fallback
        if (!lastMessageDate && main?.Created) {
          lastMessageDate = new Date(main.Created);
        }

        if (lastMessageDate) {
          const hours24 = 24 * 60 * 60 * 1000;
          const isOver24h =
            new Date().getTime() - lastMessageDate.getTime() > hours24;

          // Si pasaron más de 24h, tratar como cerrada
          if (isOver24h) {
            statusStr = WHATSTAPP_STATUS.CLOSED;
          }
        }
      }

      setChatStatus(statusStr);

      // Marcar como leído SIEMPRE (todos los estados excepto nueva)
      ActivityService.setRead({
        ids: [result.mainMessage.MessageID] as number[],
        read: "1"
      });

      setIsFreeText(openChat);
      setOriginalOpenChat(openChat);

      return history.length > 0 ? [history] : [[]];
    },
    [notifyHeaderMainMessage, isHistory, whatsappPhone]
  );

  const fetchActiveConversation = useCallback(async () => {
    if (!personId) return;

    const result = await ActivityService.getActiveConversation(
      personId.toString()
    );
    if (result instanceof Error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }

    setMatrizResults(chatList(result));
  }, [chatList, personId]);

  // 4) fetchHistoryOnce igual
  const fetchHistoryOnce = useCallback(async () => {
    if (mode?.kind !== "history") return;

    // Activar loading del teléfono mientras se carga el mensaje histórico
    setHeaderPhoneLoadingRef.current?.(true);

    const result = await ActivityService.getWhatsappChat(mode.messageId);
    if (result instanceof Error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setHeaderPhoneLoadingRef.current?.(false);
      return;
    }

    setMatrizResults(chatList(result));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ------------------------------
  // Free text change
  // ------------------------------
  const handleFreeTextChange = (val: string) => {
    setWhatsAppTemplateSelected("0");
    setWhatsAppTemplate(val);
    setActivityTypeIdState("");
    // When user types free text, ensure we're in free text mode
    setIsFreeText(true);
  };

  // ------------------------------
  // Finalizar conversación (solo si aplica)
  // ------------------------------
  const handleOpenConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleConfirmFinalizeConversation = useCallback(async () => {
    if (isFinalizingRef.current) return;
    isFinalizingRef.current = true;
    setIsFinalizing(true);
    try {
      const numericPhone = effectivePhone
        ? effectivePhone.replace(/\D/g, "")
        : "";
      if (!numericPhone) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }

      const result = await ActivityService.finalizeConverzation({
        phoneDestination: numericPhone
      });
      if (result instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }

      // Después de finalizar, obtener los mensajes actualizados para ver el estado cerrado
      if (isHistory) {
        await fetchHistoryOnce();
      } else {
        await fetchActiveConversation();
      }

      ToastService.showToast(
        TranslationService.translate.ConversationFinalized ||
          "Conversación finalizada",
        undefined,
        "success"
      );
    } catch (error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } finally {
      isFinalizingRef.current = false;
      setIsFinalizing(false);
    }
  }, [effectivePhone, isHistory, fetchHistoryOnce, fetchActiveConversation]);

  // Registrar el setter de teléfono para actualizar el header
  useEffect(() => {
    if (!onRegisterPhoneSetter) return;

    // onRegisterPhoneSetter recibe el setter que actualiza el header
    // Lo guardamos en setHeaderPhoneRef para usarlo cuando detectemos el teléfono del mensaje histórico
    onRegisterPhoneSetter(
      (setHeaderPhone: (phone: string | undefined) => void) => {
        setHeaderPhoneRef.current = setHeaderPhone;
      }
    );
  }, [onRegisterPhoneSetter]);

  // Registrar el setter de loading del teléfono para actualizar el header
  useEffect(() => {
    if (!onRegisterPhoneLoadingSetter) return;

    // onRegisterPhoneLoadingSetter recibe el setter que controla el loading del teléfono
    onRegisterPhoneLoadingSetter(
      (setHeaderPhoneLoading: (loading: boolean) => void) => {
        setHeaderPhoneLoadingRef.current = setHeaderPhoneLoading;
        // Si es un mensaje histórico, activar el loading inmediatamente
        // porque necesitamos cargar el mensaje para obtener el teléfono correcto
        if (isHistory) {
          setHeaderPhoneLoading(true);
        }
      }
    );
  }, [onRegisterPhoneLoadingSetter, isHistory]);

  // Actualizar el teléfono efectivo cuando cambia el prop para nuevos mensajes
  useEffect(() => {
    if (!isHistory) {
      setEffectivePhone(whatsappPhone);
    }
  }, [whatsappPhone, isHistory]);

  useEffect(() => {
    if (firstMount || flagRefreshChats) {
      fetchData(isHistory ? fetchHistoryOnce : fetchActiveConversation);
      setFirstMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchActiveConversation,
    fetchHistoryOnce,
    firstMount,
    isHistory,
    flagRefreshChats
  ]);

  // Helper para obtener el ID del último mensaje y su WAMessageID
  const getLastMessageIds = useCallback((): {
    messageid: string | null;
    wamessageid: string | null;
  } => {
    for (const pageItems of matrizResults) {
      if (pageItems.length > 0) {
        const lastItem = pageItems[pageItems.length - 1] as ChatItem;
        return {
          messageid: lastItem?.MessageID?.toString() ?? null,
          wamessageid: lastItem?.WAMessageID?.toString() ?? null
        };
      }
    }
    return { messageid: null, wamessageid: null };
  }, [matrizResults]);

  useEffect(() => {
    if (
      chatStatus === undefined ||
      chatStatus === WHATSTAPP_STATUS.CLOSED ||
      chatStatus === WHATSTAPP_STATUS.IS_NEW ||
      chatStatus === WHATSTAPP_STATUS.DERIVATED
    )
      return;

    const interval = setInterval(async () => {
      if (chatStatus === WHATSTAPP_STATUS.CLOSED) {
        clearInterval(interval);
        return;
      }

      const { messageid, wamessageid } = getLastMessageIds();
      if (!messageid || !wamessageid || !personId) {
        return;
      }

      // Primero verificar si hay nuevos mensajes
      const hasNewResult = await ActivityService.hasNewWhatsappMessage(
        personId,
        messageid,
        wamessageid
      );
      if (
        hasNewResult instanceof Error ||
        !hasNewResult.hasnewwhatsappmessage
      ) {
        return;
      }

      // Solo si hay nuevos mensajes, obtener la conversación completa
      if (isHistory) {
        fetchHistoryOnce();
      } else {
        fetchActiveConversation();
      }
    }, 5000);

    // limpieza al desmontar el componente o cambiar el estado
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatStatus, isHistory, getLastMessageIds, personId]);

  useEffect(() => {
    if (!onRegisterActivityTypeSetter) return;

    const setFn = (activityTypeId: number) => {
      setActivityTypeIdState(Number(activityTypeId));
    };
    const clearFn = () => setActivityTypeIdState("");

    onRegisterActivityTypeSetter(setFn, clearFn);
  }, [onRegisterActivityTypeSetter]);

  // ------------------------------
  // Socket: Actualizar conversación cuando llega mensaje nuevo
  // ------------------------------
  const handleSocketCallReceived = useCallback(
    async (payload: any) => {
      // Verificar si el mensaje corresponde a la misma conversación abierta
      const incomingPersonId =
        payload?.PersonID ?? payload?.personId ?? payload?.PersonId;

      if (!incomingPersonId || Number(incomingPersonId) !== Number(personId)) {
        // El mensaje es para otra conversación, ignorar
        return;
      }

      // Si hay polling activo (estado OPEN), no duplicar la actualización
      // En otros estados (DERIVATED, CLOSED, IS_NEW), actualizar manualmente
      const isPollingActive = chatStatus === WHATSTAPP_STATUS.OPEN;

      if (isPollingActive) {
        // El polling ya se encargará de actualizar
        return;
      }

      // Marcar el mensaje como leído automáticamente ya que el modal está abierto
      const messageId = payload?.MessageID ?? payload?.messageId ?? payload?.ID;
      if (messageId) {
        try {
          await ActivityService.setRead({
            ids: [Number(messageId)],
            read: "1"
          });
        } catch (error) {
          console.error(
            "[handleSocketCallReceived] Error al marcar como leído:",
            error
          );
        }
      }

      // Actualizar el contador del botón flotante
      getCounterInbox?.();

      // Actualizar la conversación para mostrar el último mensaje
      if (isHistory) {
        await fetchHistoryOnce();
      } else {
        await fetchActiveConversation();
      }
    },
    [
      chatStatus,
      personId,
      isHistory,
      fetchHistoryOnce,
      fetchActiveConversation,
      getCounterInbox
    ]
  );

  // Escuchar eventos de socket para nuevos mensajes
  useSocketEvent(
    WhatsAppNotificationEvent.CallReceived,
    handleSocketCallReceived
  );

  useEffect(() => {
    if (onChangeStatus) onChangeStatus(chatStatus as WHATSTAPP_STATUS);
  }, [chatStatus, onChangeStatus]);

  return (
    <div className="whatsapp-compose">
      {activityTypeLabel && (
        <div
          className="whatsapp-pill"
          title={`Tipo de gestión: ${activityTypeLabel}`}
          aria-label={`Tipo de gestión: ${activityTypeLabel}`}
        >
          <i className="fa-solid fa-list-check" aria-hidden="true" />
          <span className="whatsapp-pill__text">{activityTypeLabel}</span>
        </div>
      )}
      <div className="whatsapp-bg" />

      <div className="whatsapp-layer conversation">
        <WhatsappChat
          noMoreResults={!matrizResults.length}
          matrizResults={matrizResults}
          isFetching={isFetching}
          changeSMSSender={changeSMSSender}
          footer={
            chatStatus !== WHATSTAPP_STATUS.CLOSED ? (
              <WhatsappInputSend
                handleSend={handleCreateWhatsAppMessage}
                whatsAppTemplateText={whatsAppTemplate}
                setFreeTextChanged={handleFreeTextChange}
                disabledInput={!isFreeText}
                cleanFreeText={false}
                loading={sendingWhatsApp}
                openchat={canUseFreeText}
                onPickTemplate={handlePickTemplate}
              />
            ) : null
          }
        />

        {canShowFinalizeBtn ? (
          <button
            type="button"
            onClick={handleOpenConfirmationModal}
            className="btn-finalizar"
            disabled={isFinalizing}
          >
            {TranslationService.translate.EndConversation}
          </button>
        ) : null}
      </div>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmFinalizeConversation}
        message="¿Está seguro que desea finalizar la conversación?"
        confirmText="Confirmar"
        cancelText="Cancelar"
      />
    </div>
  );
};
